// ** React Imports
import { useState, createContext, useEffect } from 'react'

// ** Intl Provider Import
import { IntlProvider } from 'react-intl'

import { TranslationsControllerApi, settings } from '@api/backend'
const api = new TranslationsControllerApi(settings)

import Cookies from 'js-cookie';

// ** Mensajes de traducción "base" (fallback) para que los textos estén
// disponibles antes de que responda la API y como red de seguridad si a la
// API le faltase alguna clave (ej. "SAT")
import messagesEs from '@src/assets/data/locales/es.json'
import messagesEn from '@src/assets/data/locales/en.json'
import messagesDe from '@src/assets/data/locales/de.json'
import messagesFr from '@src/assets/data/locales/fr.json'
import messagesPt from '@src/assets/data/locales/pt.json'

const fallbackMessages = {
  es: messagesEs,
  en: messagesEn,
  de: messagesDe,
  fr: messagesFr,
  pt: messagesPt
}

// ** Create Context
const Context = createContext()

const IntlProviderWrapper = ({ children }) => {
  // ** States
  const idiomaCookies = Cookies.get('idioma');
  let idioma = 'es'
  if (idiomaCookies) {
    idioma = idiomaCookies
  }
  const [locale, setLocale] = useState(idioma)
  const [messages, setMessages] = useState(fallbackMessages[idioma] || {})

  useEffect(() => {
    async function getAndSetTranslations () {
      try {
        const result = await api.translationsControllerFind(JSON.stringify({where: {language: locale}}))
        const newLanguageObj = {} // {"Announcements": "Comunicados"}

        result.data?.map(translation => {
          if (translation?.value?.length) newLanguageObj[`${translation.keyName}`] = translation.value
        })
        // Combina los mensajes base con los de la API, dando prioridad a los de la API
        setMessages({ ...(fallbackMessages[locale] || {}), ...newLanguageObj })
      } catch (error) {
        console.error('Error al obtener las traducciones:', error)
        // Si falla la petición, nos quedamos con los mensajes base para no dejar la app sin traducciones
        setMessages(fallbackMessages[locale] || {})
      }
    }
    getAndSetTranslations()
  }, [locale])

  // ** Switches Language
  const switchLanguage = lang => {
    Cookies.set('idioma', lang, { expires: 365, sameSite: 'Lax', secure: window.location.protocol === 'https:' })
    setLocale(lang)
  }

  // Evita que react-intl llene la consola con "Missing message" / "Cannot format
  // message" cuando una clave aún no tiene traducción: en ese caso react-intl ya
  // muestra el id como texto de respaldo, por lo que el aviso es solo ruido.
  const manejarErrorIntl = error => {
    const mensaje = typeof error === 'string' ? error : error?.message || ''
    if (mensaje.startsWith('[React Intl] Missing message') || mensaje.startsWith('[React Intl] Cannot format message')) {
      return
    }
    console.error(error)
  }

  return (
    <Context.Provider value={{ locale, switchLanguage }}>
      <IntlProvider key={locale} locale={locale} messages={messages} defaultLocale='es' onError={manejarErrorIntl}>
        {children}
      </IntlProvider>
    </Context.Provider>
  )
}

export { IntlProviderWrapper, Context as IntlContext }
